import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Eye, EyeOff } from 'lucide-react';
import type { ColSpan, LayoutItem } from './types';
import { WIDGET_REGISTRY } from './registry';
import { Button } from '../ui';
import Pagination from '../Pagination';

const PAGE_SIZE = 9; // 3x3 grid på desktop
const LG_BREAKPOINT = '(min-width: 1024px)';

function useIsLg(): boolean {
  const [isLg, setIsLg] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(LG_BREAKPOINT).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(LG_BREAKPOINT);
    const handler = (e: MediaQueryListEvent) => setIsLg(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isLg;
}

interface Props {
  items: LayoutItem[];
  onSave: (items: LayoutItem[]) => Promise<void>;
  onClose: () => void;
}

export default function DashboardConfigModal({ items, onSave, onClose }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<LayoutItem[]>(items);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);

  function update(key: string, patch: Partial<LayoutItem>) {
    setDraft(prev =>
      prev.map(item => (item.widget_key === key ? { ...item, ...patch } : item))
    );
  }

  const isLg = useIsLg();
  // Pagination kun på desktop (lg+) — mobil/tablet scroller gennem alle widgets
  const totalPages = isLg ? Math.ceil(WIDGET_REGISTRY.length / PAGE_SIZE) : 1;
  const pagedRegistry = isLg
    ? WIDGET_REGISTRY.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : WIDGET_REGISTRY;

  async function handleSave() {
    setSaving(true);
    await onSave(draft);
    setSaving(false);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />

      <div className="relative bg-cream-50 rounded-card shadow-card-md w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-ink-100">
          <div>
            <h2 className="text-lg font-semibold text-ink-900 tracking-tight">
              {t('dashboard.config_title', { defaultValue: 'Tilpas dashboard' })}
            </h2>
            <p className="text-sm text-ink-500 mt-0.5">
              {t('dashboard.config_subtitle', { defaultValue: 'Aktivér eller skjul widgets, og vælg hvor brede de skal være.' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 -mr-2 rounded-lg text-ink-300 hover:text-ink-700 hover:bg-cream-100 transition-colors"
            aria-label={t('modal.close', { defaultValue: 'Luk' })}
          >
            <X size={18} />
          </button>
        </div>

        {/* Widget grid */}
        <div className="overflow-y-auto flex-1 px-6 py-5 bg-cream-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pagedRegistry.map(def => {
              const item = draft.find(i => i.widget_key === def.key);
              if (!item) return null;

              const colOptions: ColSpan[] = ([1, 2, 3, 4] as ColSpan[]).filter(
                n => n >= def.minColSpan && n <= def.maxColSpan
              );
              const isActive = item.visible_web || item.visible_mobile;

              return (
                <div
                  key={def.key}
                  className={`rounded-card p-4 transition-all flex flex-col ${
                    isActive
                      ? 'bg-cream-50 border border-sage-300 shadow-card-sm'
                      : 'bg-cream-50 border border-ink-100 opacity-70'
                  }`}
                >
                  {/* Title row */}
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <h3 className="text-sm font-semibold text-ink-900 tracking-tight leading-snug">
                      {t(def.titleKey)}
                    </h3>
                    {isActive && (
                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-sage-100 text-sage-700 rounded">
                        Aktiv
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-xs text-ink-500 leading-relaxed mb-4 flex-1">
                    {t(def.descriptionKey, { defaultValue: '' })}
                  </p>

                  {/* Column selector — over visibility */}
                  {colOptions.length > 1 && (
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-ink-500">
                        {t('dashboard.config_columns', { defaultValue: 'Bredde' })}
                      </span>
                      <div className="inline-flex gap-0.5 bg-cream-200 p-0.5 rounded-lg">
                        {colOptions.map(n => (
                          <button
                            key={n}
                            onClick={() => update(def.key, { col_span: n })}
                            className={`w-7 h-6 text-[11px] font-semibold rounded-md transition-colors ${
                              item.col_span === n
                                ? 'bg-cream-50 text-ink-900 shadow-card-sm'
                                : 'text-ink-500 hover:text-ink-900'
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Height selector */}
                  {def.supportsHeightChoice && (
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-ink-500">
                        {t('dashboard.config_height', { defaultValue: 'Højde' })}
                      </span>
                      <div className="inline-flex gap-0.5 bg-cream-200 p-0.5 rounded-lg">
                        {(['low', 'medium', 'high', 'auto'] as const).map(h => (
                          <button
                            key={h}
                            onClick={() => update(def.key, { height: h })}
                            className={`px-2 h-6 text-[11px] font-semibold rounded-md transition-colors ${
                              item.height === h
                                ? 'bg-cream-50 text-ink-900 shadow-card-sm'
                                : 'text-ink-500 hover:text-ink-900'
                            }`}
                          >
                            {t(`dashboard.config_height_${h}`, { defaultValue: h })}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Visibility toggles — under bredde */}
                  <div className="flex items-center gap-2">
                    <VisibilityToggle
                      label={t('dashboard.config_web', { defaultValue: 'Web' })}
                      active={item.visible_web}
                      onClick={() => update(def.key, { visible_web: !item.visible_web })}
                    />
                    <VisibilityToggle
                      label={t('dashboard.config_mobile', { defaultValue: 'Mobil' })}
                      active={item.visible_mobile}
                      onClick={() => update(def.key, { visible_mobile: !item.visible_mobile })}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer med pagination */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-ink-100">
          <div className="flex-1">
            {totalPages > 1 && (
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
              {t('dashboard.config_cancel', { defaultValue: 'Annullér' })}
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              {saving
                ? t('dashboard.config_saving', { defaultValue: 'Gemmer…' })
                : t('dashboard.config_save', { defaultValue: 'Gem' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Visibility toggle med ikon ────────────────────────────────────────────────

function VisibilityToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs font-medium transition-colors flex-1 justify-center ${
        active
          ? 'bg-sage-100 text-sage-700'
          : 'bg-cream-200 text-ink-500 hover:bg-ink-100'
      }`}
    >
      {active ? <Eye size={12} /> : <EyeOff size={12} />}
      {label}
    </button>
  );
}
